import { useCallback, useEffect, useState } from 'react';
import { Grid3x3, Maximize, Minus, Plus, Ruler } from 'lucide-react';
import { Canvas } from './ui/Canvas';
import { zoomToFit } from './render/CanvasRenderer';
import { Rail } from './ui/Rail';
import { SidePanel } from './ui/SidePanel';
import { SectionId } from './ui/sections';
import { TopBar, TabStrip } from './ui/TopBar';
import { StatusBar } from './ui/StatusBar';
import { RightPanel } from './ui/RightPanel';
import { CommandPalette, useCommands } from './ui/CommandPalette';
import { NewDocumentDialog } from './ui/dialogs/NewDocumentDialog';
import { ExportDialog } from './ui/dialogs/ExportDialog';
import { ImageImportDialog } from './ui/dialogs/ImageImportDialog';
import { PreviewDialog } from './ui/dialogs/PreviewDialog';
import { SearchReplaceDialog } from './ui/dialogs/SearchReplaceDialog';
import { RecoveryDialog } from './ui/dialogs/RecoveryDialog';
import { useShortcuts } from './ui/useShortcuts';
import { Tip, TooltipLayer } from './ui/widgets/primitives';
import { Notifications } from './ui/widgets/Notifications';
import { useEditor } from './state/store';
import { downloadText, pickFile, readFileText } from './ui/fileIO';
import { listSnapshots, useAutosave } from './state/autosave';
import { AccountDialog } from './auth/AccountDialog';
import { AdminDialog } from './auth/AdminDialog';
import { CloudDialog } from './auth/CloudDialog';

export type DialogId =
  | 'new'
  | 'export'
  | 'image'
  | 'preview'
  | 'search'
  | 'save'
  | 'open'
  | 'account'
  | 'admin'
  | 'cloud-open'
  | 'cloud-save'
  | null;

export function App(): JSX.Element {
  const [dialog, setDialog] = useState<DialogId>(null);
  const [commandsOpen, setCommandsOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [section, setSection] = useState<SectionId>('draw');
  // Offer recovery once, and only when there is actually something to recover.
  const [recovery, setRecovery] = useState(() => listSnapshots().length > 0);

  /**
   * Save and open are actions rather than dialogs, but they share the shortcut
   * and command-palette entry points, so they route through the same id.
   */
  const openDialog = useCallback((id: string) => {
    const store = useEditor.getState();
    if (id === 'save') {
      const tab = store.active();
      const name = tab.doc.name.trim() || 'untitled';
      downloadText(`${name}.tui.json`, store.projectJson(true), 'application/json');
      tab.dirty = false;
      store.notify(`Saved ${name}.tui.json`, 'success');
      return;
    }
    if (id === 'open') {
      void (async () => {
        const file = await pickFile('.json,.tui.json,application/json');
        if (file) store.loadProject(await readFileText(file), file.name);
      })();
      return;
    }
    if (id === 'templates') {
      setSection('templates');
      return;
    }
    setDialog(id as DialogId);
  }, []);

  const commands = useCommands(openDialog);
  useShortcuts(openDialog, () => setCommandsOpen(true));
  useAutosave();
  useBeforeUnload();

  return (
    <TooltipLayer>
      <div className="app">
        <TopBar
          onOpenDialog={openDialog}
          onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
          onOpenCommands={() => setCommandsOpen(true)}
          sidebarCollapsed={sidebarCollapsed}
        />

        <div className="workspace">
          <Rail active={section} onSelect={setSection} />
          <SidePanel section={section} />
          <main className="stage">
            <TabStrip />
            <div className="canvas-frame">
              <Canvas />
              <ZoomDock />
            </div>
          </main>
          <RightPanel collapsed={sidebarCollapsed} section={section} />
        </div>

        <StatusBar />

        {dialog === 'new' && <NewDocumentDialog onClose={() => setDialog(null)} />}
        {dialog === 'export' && <ExportDialog onClose={() => setDialog(null)} />}
        {dialog === 'image' && <ImageImportDialog onClose={() => setDialog(null)} />}
        {dialog === 'preview' && <PreviewDialog onClose={() => setDialog(null)} />}
        {dialog === 'search' && <SearchReplaceDialog onClose={() => setDialog(null)} />}

        {recovery && <RecoveryDialog onClose={() => setRecovery(false)} />}
        {commandsOpen && <CommandPalette commands={commands} onClose={() => setCommandsOpen(false)} />}

        {dialog === 'account' && <AccountDialog onClose={() => setDialog(null)} />}
        {dialog === 'admin' && <AdminDialog onClose={() => setDialog(null)} />}
        {dialog === 'cloud-save' && <CloudDialog mode="save" onClose={() => setDialog(null)} />}
        {dialog === 'cloud-open' && <CloudDialog mode="open" onClose={() => setDialog(null)} />}

        <Notifications />
      </div>
    </TooltipLayer>
  );
}

/** Zoom and view toggles, floating over the canvas. */
function ZoomDock(): JSX.Element {
  const zoom = useEditor((s) => s.renderOptions.zoom);
  const showGrid = useEditor((s) => s.renderOptions.showGrid);
  const showRulers = useEditor((s) => s.renderOptions.showRulers);
  const set = useEditor((s) => s.setRenderOptions);

  /**
   * Scales the document until all of it is on screen.
   *
   * A design wider than the window is simply cut off, with nothing to say so —
   * you can be looking at two thirds of your work and not know it. The
   * viewport is measured from the DOM rather than tracked in the store because
   * it changes with the window, the panels and the rulers, and reading it at
   * the moment of the click is the only way to be right about all three.
   */
  const fitToWindow = () => {
    const host = document.querySelector('.canvas-host');
    if (!host) return;
    const options = useEditor.getState().renderOptions;
    const doc = useEditor.getState().active().doc;
    const rect = host.getBoundingClientRect();
    set({
      zoom: zoomToFit(doc, rect, {
        fontSize: options.fontSize,
        cellAspect: options.cellAspect,
        showRulers: options.showRulers,
      }),
    });
  };

  return (
    <div className="zoom-dock">
      <Tip label="Show the cell grid  ·  Ctrl+'" side="top">
        <button
          className={`btn icon${showGrid ? ' active' : ''}`}
          aria-label="Toggle grid"
          aria-pressed={showGrid}
          onClick={() => set({ showGrid: !showGrid })}
        >
          <Grid3x3 size={14} />
        </button>
      </Tip>
      <Tip label="Show rulers along the top and left edges" side="top">
        <button
          className={`btn icon${showRulers ? ' active' : ''}`}
          aria-label="Toggle rulers"
          aria-pressed={showRulers}
          onClick={() => set({ showRulers: !showRulers })}
        >
          <Ruler size={14} />
        </button>
      </Tip>
      <span className="divider" />
      <Tip label="Zoom out  ·  Ctrl+−" side="top">
        <button
          className="btn icon"
          aria-label="Zoom out"
          onClick={() => set({ zoom: Math.max(0.25, zoom - 0.15) })}
        >
          <Minus size={14} />
        </button>
      </Tip>
      <Tip label="Back to 100%  ·  Ctrl+0" side="top">
        <button
          className="value"
          onClick={() => set({ zoom: 1 })}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          {Math.round(zoom * 100)}%
        </button>
      </Tip>
      <Tip label="Fit the whole design in the window" side="top">
        <button className="btn icon" aria-label="Fit to window" onClick={fitToWindow}>
          <Maximize size={14} />
        </button>
      </Tip>
      <Tip label="Zoom in  ·  Ctrl++" side="top">
        <button
          className="btn icon"
          aria-label="Zoom in"
          onClick={() => set({ zoom: Math.min(6, zoom + 0.15) })}
        >
          <Plus size={14} />
        </button>
      </Tip>
    </div>
  );
}

/** Warns before leaving with unsaved work. */
function useBeforeUnload(): void {
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (useEditor.getState().tabs.some((t) => t.dirty)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}
